/**
 * Interactive add-on (Upgrade & Save)
 * Handles in-block variant selects, dynamic pricing, inventory labels,
 * and syncs selected add-on variant into ATC via data attributes.
 */
(function () {
  function formatMoney(cents, format) {
    const moneyFormat = format || window.money_format || '${{amount}}';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }
    const amount = (Number(cents) / 100).toFixed(2);
    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(Number(cents) / 100)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','));
  }

  function parseJson(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function findVariant(variants, options) {
    if (!variants || !variants.length) return null;
    return (
      variants.find((v) => {
        return options.every((opt, i) => {
          const key = 'option' + (i + 1);
          return !opt || v[key] === opt;
        });
      }) || null
    );
  }

  function getSelectedOptions(col) {
    const selects = col.querySelectorAll('[data-ia-option]');
    if (!selects.length) {
      const variants = parseJson(col.querySelector('[data-ia-variants]')) || [];
      const v = variants[0];
      return v ? [v.option1, v.option2, v.option3].filter(Boolean) : [];
    }
    const opts = [];
    selects.forEach((sel) => {
      const idx = parseInt(sel.dataset.optionIndex, 10) || 0;
      opts[idx] = sel.value;
    });
    return opts;
  }

  function updateCol(col, config, root) {
    const variants = parseJson(col.querySelector('[data-ia-variants]')) || [];
    const options = getSelectedOptions(col);
    const variant = findVariant(variants, options) || variants.find((v) => v.available) || variants[0];
    if (!variant) return null;

    col.dataset.variantId = String(variant.id);

    const media = col.querySelector('[data-ia-media] img, [data-ia-media] .interactive-addon__img');
    if (media && variant.featured_image) {
      if (media.tagName === 'IMG') {
        media.src = variant.featured_image;
        media.srcset = '';
      }
    }

    const stock = col.querySelector('[data-ia-stock]');
    if (stock && config.showInventory !== false) {
      const available = !!variant.available;
      const text = available
        ? stock.dataset.instockText || config.instockText || 'In-stock, ships in 1-2 business days'
        : stock.dataset.outstockText || config.soldoutText || 'Sold Out';
      const color = available
        ? stock.dataset.instockColor || config.instockColor || '#34A853'
        : stock.dataset.outstockColor || config.soldoutColor || '#d32f2f';

      const textEl = stock.querySelector('[data-ia-stock-text]') || stock.querySelector('.instock-text');
      if (textEl) {
        textEl.textContent = text;
        textEl.style.setProperty('--instock-color', color);
      }
    }

    // Sync main product form when left column changes
    if (col.dataset.iaSide === 'main') {
      const form = root.closest('.main-product-section')?.querySelector('form[action*="/cart/add"]') ||
        document.querySelector('form[action*="/cart/add"]');
      const idInput = form?.querySelector('[name="id"]');
      if (idInput && String(idInput.value) !== String(variant.id)) {
        idInput.value = variant.id;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    return variant;
  }

  function updatePricing(root, mainVariant, addonVariant, config) {
    const totalEl = root.querySelector('[data-ia-total]');
    const compareEl = root.querySelector('[data-ia-compare]');
    const badgeEl = root.querySelector('[data-ia-badge]');
    const format = root.dataset.moneyFormat || window.money_format;

    if (!mainVariant) return;

    const mainPrice = mainVariant.price || 0;
    const mainCompare = mainVariant.compare_at_price > mainPrice ? mainVariant.compare_at_price : mainPrice;
    const addonPrice = addonVariant ? addonVariant.price || 0 : 0;
    const addonCompare = addonVariant
      ? addonVariant.compare_at_price > addonPrice
        ? addonVariant.compare_at_price
        : addonPrice
      : 0;

    const total = mainPrice + addonPrice;
    const compare = mainCompare + addonCompare;

    if (totalEl) totalEl.textContent = formatMoney(total, format);

    if (compareEl) {
      if (compare > total) {
        compareEl.textContent = formatMoney(compare, format);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    if (badgeEl) {
      if (compare > total) {
        const pct = Math.round(((compare - total) / compare) * 100);
        badgeEl.textContent =
          (config.badgePrefix || 'Full') + ' – (Over ' + pct + '% off!)';
        badgeEl.style.display = '';
      } else {
        badgeEl.style.display = 'none';
      }
    }

    // Per-side value text (only auto-update when merchant left value blank)
    updateValueLabel(root.querySelector('[data-ia-side="main"]'), mainCompare, mainPrice, config.mainValueText, format);
    if (addonVariant) {
      updateValueLabel(
        root.querySelector('[data-ia-side="addon"]'),
        addonCompare,
        addonPrice,
        config.addonValueText,
        format
      );
    }
  }

  function updateValueLabel(col, compare, price, customText, format) {
    if (!col) return;
    const el = col.querySelector('[data-ia-value]');
    if (!el) return;
    if (customText) {
      el.innerHTML = customText;
      el.style.display = '';
      return;
    }
    if (compare > price) {
      el.innerHTML = '<s>' + formatMoney(compare, format) + ' value</s>';
      el.style.display = '';
    } else {
      el.style.display = 'none';
    }
  }

  function refresh(root) {
    const config = parseJson(root.querySelector('[data-ia-config]')) || {};
    const mainCol = root.querySelector('[data-ia-side="main"]');
    const addonCol = root.querySelector('[data-ia-side="addon"]');
    const mainVariant = mainCol ? updateCol(mainCol, config, root) : null;
    const addonVariant = addonCol ? updateCol(addonCol, config, root) : null;
    updatePricing(root, mainVariant, addonVariant, config);
    const toggle = root.querySelector('[data-ia-toggle]');
    const isActive = !!(toggle && toggle.checked && !toggle.disabled);
    root.classList.toggle('is-active', isActive);
    root.setAttribute('data-ia-active', isActive ? 'true' : 'false');
    if (addonCol && addonVariant) {
      addonCol.setAttribute('data-variant-id', String(addonVariant.id));
    }
  }

  function initRoot(root) {
    if (root.dataset.iaReady === 'true') return;
    root.dataset.iaReady = 'true';

    root.addEventListener('change', (e) => {
      if (e.target.matches('[data-ia-option], [data-ia-toggle]')) {
        refresh(root);
      }
    });

    // Click on switch/label should reliably toggle (some themes intercept label clicks)
    const toggleWrap = root.querySelector('.interactive-addon__toggle-wrap');
    const toggleInput = root.querySelector('[data-ia-toggle]');
    if (toggleWrap && toggleInput && !toggleInput.disabled) {
      toggleWrap.addEventListener('click', (e) => {
        if (e.target === toggleInput) return;
        e.preventDefault();
        toggleInput.checked = !toggleInput.checked;
        toggleInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    // Keep left column in sync when main PDP variant changes elsewhere
    const form =
      root.closest('.main-product-section')?.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]');
    const idInput = form?.querySelector('[name="id"]');
    if (idInput) {
      idInput.addEventListener('change', () => {
        const mainCol = root.querySelector('[data-ia-side="main"]');
        if (!mainCol) return;
        const variants = parseJson(mainCol.querySelector('[data-ia-variants]')) || [];
        const variant = variants.find((v) => String(v.id) === String(idInput.value));
        if (!variant) return;
        mainCol.dataset.variantId = String(variant.id);
        const selects = mainCol.querySelectorAll('[data-ia-option]');
        selects.forEach((sel) => {
          const idx = parseInt(sel.dataset.optionIndex, 10) || 0;
          const val = variant['option' + (idx + 1)];
          if (val) sel.value = val;
        });
        refresh(root);
      });
    }

    refresh(root);
  }

  function initAll() {
    document.querySelectorAll('[data-interactive-addon]').forEach(initRoot);
  }

  /** Selected interactive add-on variant ids when toggle is on */
  window.getInteractiveAddonIds = function getInteractiveAddonIds() {
    const ids = [];
    document.querySelectorAll('[data-interactive-addon]').forEach((root) => {
      const toggle = root.querySelector('[data-ia-toggle]');
      if (!toggle || !toggle.checked || toggle.disabled) return;
      const addonCol = root.querySelector('[data-ia-side="addon"]');
      const id = addonCol?.dataset?.variantId;
      if (id) ids.push(id);
    });
    return ids;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
